import { BaseMapper, Undefinable } from '@ps/utils';
import {
  AdvancedQualification,
  SurveyQualificationDependency,
  toChildrenLegacyQualificationCode,
} from '@ps/survey-utils';

import {
  AdvancedTargetingDependencyDto,
  GetAdvancedTargetingQuestionDto,
} from '../dtos/advanced-qualification.dto';
import { QualificationCategoryMapper } from './qualification-category.mapper';

export class AdvancedQualificationMapper extends BaseMapper<
  GetAdvancedTargetingQuestionDto,
  AdvancedQualification
> {
  private readonly _categoryMapper = new QualificationCategoryMapper();

  // Questions without a qualification_id all map to an empty attributeId, and callers
  // match a selection by attributeId — so every malformed row would collide on one id.
  public override fromDtoList(
    dtos: GetAdvancedTargetingQuestionDto[],
  ): AdvancedQualification[] {
    return dtos
      .filter((dto) => Boolean(dto.qualification_id?.length))
      .map((dto) => this.fromDto(dto));
  }

  public fromDto(dto: GetAdvancedTargetingQuestionDto): AdvancedQualification {
    const attributeId = String(dto.qualification_id?.[0] ?? '');

    return {
      attributeId,
      desc: dto.question_description,
      category: this._categoryMapper.fromDtoList(dto.question_category ?? []),
      options: [...(dto.answers?.[attributeId]?.answer_data ?? [])]
        .sort((first, second) => first.sortId - second.sortId)
        .map((answer) => ({ id: String(answer.id), text: answer.text })),
      buyerText: dto.buyer_text,
      hasDependency: Boolean(dto.hasDependency),
      isDeprecated: Boolean(dto.deprecated),
      ...this._dependencyFields(dto.dependency),
    };
  }

  private _dependencyFields(
    dependency: Undefinable<AdvancedTargetingDependencyDto>,
  ): Partial<Pick<AdvancedQualification, 'dependency'>> {
    if (!dependency) {
      return {};
    }

    return { dependency: this._toDependency(dependency) };
  }

  private _toDependency(
    dependency: AdvancedTargetingDependencyDto,
  ): SurveyQualificationDependency {
    return {
      respondentQuestionId: dependency.respondent_question_id,
      parentQualificationId: toChildrenLegacyQualificationCode(
        dependency.parentQualId,
      ),
      matchCriteria: dependency.matchCriteria,
      repeat: dependency.repeat,
      condition: dependency.condition,
    };
  }
}
